import type Experiments from '@wix/wix-experiments';
import { PRESETS } from '../utils/presets';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_LENGTH_6,
} from '../utils/validators';

export const SIGNUP_EMAIL_STORAGE_KEY = 'signup-email-code-address';
export const FLOW_EMAIL_STORAGE_KEY = 'login-flow-email';

export const RESEND_INDICATION_TTL_TIMER = 30000;
export const RESEND_INDICATION_TTL_TIMER_FOR_WIX_APP_ENFORCEMENT = 10000;

export const CONDUCTOR_FT = {
  ENABLED: 'B',
  DISABLED: 'A',
} as const;

export const EXPERIMENTS = {
  CREATE_PASSWORD_OFFER: 'specs.ident.createPasswordOffer',
  TWO_FA_AGGRESSIVE_OFFER: 'specs.ident.2faAggresiveOffer',
  SHOULD_SHOW_POST_LOGIN_OWNER_APP2_FA:
    'specs.ident.ShouldShowPostLoginOwnerApp2Fa',
  SHOW_ERROR_MESSAGE_FOR_FACEBOOK2_FA_USERS:
    'specs.ident.showErrorMessageForFacebook2FaUsers',
  SHOULD_SHOW_WIX_APP_ENFORCEMENT: 'specs.ident.shouldShowWixAppEnforcement',
  SHOULD_STEPUP_EDIT_2FA: 'specs.ident.shouldStepupEdit2FA',
  SHOULD_ACTIVATE_NEW_COLORS_BRANDING:
    'specs.ident.ShouldActivateNewColorBrandingForAccountSettings',
  SHOULD_USE_NEW_POST_LOGIN_URLS: 'specs.ident.shouldUseNewPostLoginUrls',
  SHOULD_ENABLE_WIXEL: 'specs.ident.shouldEnableWixel',
  IGNORE_REFERRAL_INFO_FOR_POST_LOGIN:
    'specs.ident.IgnoreReferralInfoForPostLogin',
  PRIORITIZE_USER_TYPE_OVER_QUERY_PARAMS:
    'specs.ident.prioritizeUserTypeOverQueryParam',
  ENABLE_PASSKEY_LOGIN: 'specs.ident.enablePasskeyLogin',
  ENABLE_USERNAMELESS_PASSKEY_LOGIN:
    'specs.ident.enableUsernamelessPasskeyLogin',
  SHOULD_ACTIVATE_WIX_2025_PRESET: 'specs.users.ShouldActivateWix2025Preset',
  SHOULD_ACTIVATE_WIX_2025_FOR_BRANDS:
    'specs.ident.shouldActivateWix2025ForBrands',
  SHOULD_ACTIVATE_WIX_2025_ENFORCEMENT_REDESIGN:
    'shouldActivateWix2025EnforcementRedesign',
  SHOULD_ACTIVATE_WIX_2025_DEALER_OFFERS_REDESIGN:
    'shouldActivateWix2025DealerOffersRedesign',
  SHOULD_USE_OTP_INPUT_IN_CODE_STEPS: 'shouldUseOtpInputInCodeSteps',
  SHOULD_SHOW_POST_LOGIN_PASSKEY_OFFER:
    'specs.ident.ShouldShowPostLoginPasskeyOffer',
  ENABLE_2FA_ENFORCEMENT: 'enable2faEnforcement',
  ENABLE_8CHAR_PASSWORD: 'shouldEnforceMinPassword8CharsWeb',
  REMOVE_SIGNUP_CONFIRM_FIELDS: 'shouldRemoveSignupConfirmFields',
  ENABLE_2FA_LOBBY: 'enable2faLobby',
  ENABLE_2FA_LOBBY_WHATSAPP: 'enable2faLobbyWhatsApp',
  ENABLE_PUSH_REMEMBER_DEVICE: 'enablePushRememberDevice',
  SHOULD_USE_REDIRECT_TO_AS_SSO_TARGET_URL: 'shouldUseRedirectToAsSsoTargetUrl',
  EAVS_EMAIL_SCORE_SIGNAL: 'eavsEmailScoreSignal',
  SYMPHONY_HIDE_FACEBOOK_UNLESS_CONNECTED:
    'shouldSymphonyHideFacebookUnlessConnected',
  ENABLE_STEP_UP_ON_POST_LOGIN_PASSKEY_ENROLL:
    'shouldEnableStepUpOnPostLoginPasskeyEnroll',
  SHOW_LAST_USED_AUTH_METHOD: 'showLastUsedAuthMethod',
  SIGNUP_WITH_EMAIL_CODE: 'shouldEnableSignupWithEmailCode',
  LOGIN_WITH_EMAIL_CODE: 'shouldEnableLoginWithEmailCode',
  SHOW_CAPTCHA_CHECKBOX_PROMPT: 'showCaptchaCheckboxPrompt',
  IDP_REDIRECT_WHEN_POPUP_BLOCKED:
    'shouldFallbackToIdpRedirectWhenPopupBlocked',
  ROUTE_BACK_TO_LOGIN_IN_APP: 'shouldRouteBackToLoginInApp',
  SEND_DIRECT_LOGIN_TO_EMAIL_STEP: 'shouldSendDirectLoginToEmailStep',
  SHOW_CHANGE_EMAIL_ON_PASSWORD_PAGE: 'shouldShowChangeEmailOnPasswordPage',
};

/**
 * Returns the minimum password length, gated by the
 * `shouldEnforceMinPassword8CharsWeb` Conductor FT.
 * FT on (variant "B") -> 8, otherwise the legacy 6.
 */
export const getPasswordMinLength = (experiments: Experiments): number =>
  experiments.get(EXPERIMENTS.ENABLE_8CHAR_PASSWORD) === CONDUCTOR_FT.ENABLED
    ? PASSWORD_MIN_LENGTH
    : PASSWORD_MIN_LENGTH_6;

/**
 * A/B test, seed CLIENT_ID. Variant "B" removes the confirm-email and
 * confirm-password fields from the signup form; control ("A"/unset) keeps both
 * confirmation inputs. Until the spec is live, `get` returns undefined -> control.
 */
export const shouldRemoveSignupConfirmFields = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.REMOVE_SIGNUP_CONFIRM_FIELDS) ===
  CONDUCTOR_FT.ENABLED;

/** Variant B enables email-code registration; A/unset preserves password signup. */
export const shouldEnableSignupWithEmailCode = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.SIGNUP_WITH_EMAIL_CODE) === CONDUCTOR_FT.ENABLED;

/**
 * Variant B offers logging in with an email code, and gives the login password
 * form a back button (to the code screen, or the two-step login's email step);
 * A/unset keeps password-only login, with a back button only from signup.
 */
export const shouldEnableLoginWithEmailCode = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.LOGIN_WITH_EMAIL_CODE) === CONDUCTOR_FT.ENABLED;

/**
 * Variant B sends the user through a full-page IdP redirect when the browser
 * blocks the IdP popup; A/unset shows the login error as before.
 */
export const shouldFallbackToIdpRedirect = (
  experiments?: Experiments,
): boolean =>
  experiments?.get(EXPERIMENTS.IDP_REDIRECT_WHEN_POPUP_BLOCKED) ===
  CONDUCTOR_FT.ENABLED;

/**
 * Variant B keeps the login in this page after an account is picked in the
 * account selector (password form, or social providers for a social-only
 * account), in the old app too, with the picked account kept in the page.
 * A/unset is as before: the old app redirects to the login page, the new app
 * opens the password form.
 */
export const shouldRouteBackToLoginInApp = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.ROUTE_BACK_TO_LOGIN_IN_APP) ===
  CONDUCTOR_FT.ENABLED;

/**
 * Variant B sends a page opened straight on the password form to the login
 * email step, keeping the URL's query, so every login there starts from a
 * looked-up email. A/unset opens the password form as before.
 */
export const shouldSendDirectLoginToEmailStep = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.SEND_DIRECT_LOGIN_TO_EMAIL_STEP) ===
  CONDUCTOR_FT.ENABLED;

/**
 * Variant B shows the password form's email read-only on every path, with a
 * Change button that goes back to the email step with the email filled in.
 * A/unset: read-only only after an account pick, otherwise editable with a
 * clear button.
 */
export const shouldShowChangeEmailOnPasswordPage = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.SHOW_CHANGE_EMAIL_ON_PASSWORD_PAGE) ===
  CONDUCTOR_FT.ENABLED;

export const shouldEnable2faLobby = (experiments: Experiments): boolean =>
  experiments.get(EXPERIMENTS.ENABLE_2FA_LOBBY) === CONDUCTOR_FT.ENABLED;

export const isWhatsAppLobbyEnabled = (experiments: Experiments): boolean =>
  experiments.get(EXPERIMENTS.ENABLE_2FA_LOBBY_WHATSAPP) ===
  CONDUCTOR_FT.ENABLED;

export const shouldEnablePushRememberDevice = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.ENABLE_PUSH_REMEMBER_DEVICE) ===
  CONDUCTOR_FT.ENABLED;

export const isEavsEmailScoreSignalEnabled = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.EAVS_EMAIL_SCORE_SIGNAL) === CONDUCTOR_FT.ENABLED;

/**
 * Conductor FT. When on (variant "B"), the social login button matching the
 * method that finished the previous successful authentication on this browser
 * gets a "Last used" marker. Off / unset -> control: no marker.
 *
 * Bare Conductor key (no `specs.ident.` Petri namespace), so it is read with
 * `get(...) === "B"`. NOT `experiments.enabled(...)` — that compares against
 * the string "true" and is therefore always false for an A/B variant.
 */
export const shouldShowLastUsedAuthMethod = (
  experiments: Experiments,
): boolean =>
  experiments?.get(EXPERIMENTS.SHOW_LAST_USED_AUTH_METHOD) ===
  CONDUCTOR_FT.ENABLED;

/**
 * Symphony-only Conductor FT. When on (variant "B"), the Facebook SSO button is
 * hidden unless the account returned by `/userAccountsByEmail` actually has a
 * facebook social connection (i.e. `"facebook"` is in
 * `socialConnectionsProviders`). Off / unset -> control: Facebook keeps showing
 * per preset, as before. Only takes effect for the Symphony userType.
 *
 * Bare Conductor key (no `specs.ident.` Petri namespace) — created in the
 * Conductor tool, read here via `experiments.get(...) === "B"`.
 */
export const shouldHideFacebookUnlessConnected = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.SYMPHONY_HIDE_FACEBOOK_UNLESS_CONNECTED) ===
  CONDUCTOR_FT.ENABLED;

export const shouldEnableStepUpOnPostLoginPasskeyEnroll = (
  experiments: Experiments,
): boolean =>
  experiments.get(EXPERIMENTS.ENABLE_STEP_UP_ON_POST_LOGIN_PASSKEY_ENROLL) ===
  CONDUCTOR_FT.ENABLED;

export const BI_SCREEN_NAMES = {
  CHOOSE_DEVICE: 'choose_device',
  CONFIRM_DEVICE: 'confirm_device',
  CANCEL_SETUP: 'cancel_setup',
  FAILED_TO_CONFIRM_DEVICE: 'failed_to_confirm_device',
  LOGIN_REQUEST_DENIED: 'login_request_denied',
  CHOOSE_VERIFICATION_METHOD: 'choose_verification_method',
  WIX_APP_ENFORCEMENT: 'wix_app_enforcement',
  APPROVE_DEVICE: 'approve device',
  ENFORCEMENT_FAILED_TO_CONFIRM_DEVICE: 'failed_to_confirm_device',
  ENFORCEMENT_LOGIN_REQUEST_WAS_DENIED: 'Login request was denied',
};

export const BI_BUTTON_NAMES = {
  NEXT: 'Next',
  MAYBE_LATER: 'Maybe Later',
  ACCOUNT_SETTINGS: 'Account Settings',
  BACK: 'Back',
  CANCEL: 'Cancel',
  RESEND_LOGIN_REQUEST: 'Resend Login Request',
  CANCEL_SETUP: 'Cancel Setup',
  X_BUTTON: 'X_button',
  SET_UP: 'Set Up',
  LEARN_MORE: 'Learn more',
  CONFIRM_EMAIL: 'confirm',
  RESEND_EMAIL: 'resend',
  SKIP_FOR_NOW: 'Skip for now',
};

export const BI_ORIGIN = {
  TWO_FA_ENFORCEMENT: '2fa_enforcement',
  TWO_FA_ENFORCEMENT_SECOND_METHOD: '2fa_enforcement_second_method',
  POST_SIGNUP: 'Post Signup',
};

export const BI_ERROR_TYPE = {
  EXPIRED_CODE: 'code expired',
  INCORRECT_CODE: 'code incorrect',
};

export const CUSTOMER_CARE_BLOCKED_ACCOUNT_BOT_URL =
  'https://www.wix.com/support-chatbot?nodeId=aecd4efb-caf3-404d-b5f7-6f9a808f8853';

export const FUNNELS_WITHOUT_SSO = [
  PRESETS.WIXEL,
  PRESETS.EDUCATION_STUDENT,
  PRESETS.EDUCATION_TEACHER,
];
